import * as React from "react";
import { useQuery } from "@tanstack/react-query";
import {
    Activity,
    ArrowDownRight,
    ArrowUpRight,
    LineChart as LineChartIcon,
    Minus,
} from "lucide-react";
import {
    Area,
    AreaChart,
    Bar,
    BarChart,
    CartesianGrid,
    Cell,
    Legend,
    Line,
    Pie,
    PieChart,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from "recharts";
import { getAnalytics, getAnalyticsRealtime, type AnalyticsRange } from "@/api/domains";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Spinner } from "@/components/ui/spinner";
import { WorldMap } from "@/components/world-map";
import { cn } from "@/lib/utils";
import type { AnalyticsReportDto, RealtimeReportDto } from "@/types/api";

interface Props {
    domainId: number;
    gaCode: string | null;
    gaPropertyId: string | null;
}

// Brand colour palette for charts. Tailwind primary (slate/cyan-ish in this theme)
// + 4 supporting tones derived from the same hue family.
const PALETTE = ["#2563eb", "#0ea5e9", "#06b6d4", "#10b981", "#84cc16", "#eab308", "#f97316", "#ef4444"];
const SESSIONS_COLOUR = "#2563eb";
const USERS_COLOUR = "#0ea5e9";
const CONVERSIONS_COLOUR = "#10b981";

export function GaDashboard({ domainId, gaCode, gaPropertyId }: Props): React.ReactNode {
    const [range, setRange] = React.useState<AnalyticsRange>("7d");
    const reportQuery = useQuery({
        queryKey: ["domain", domainId, "analytics", range],
        queryFn: () => getAnalytics(domainId, range),
        enabled: gaPropertyId != null,
        staleTime: 60_000,
    });
    const realtimeQuery = useQuery({
        queryKey: ["domain", domainId, "realtime"],
        queryFn: () => getAnalyticsRealtime(domainId),
        enabled: gaPropertyId != null,
        refetchInterval: 30_000,
        staleTime: 15_000,
    });

    return (
        <Card>
            <CardHeader className="flex flex-row items-start justify-between gap-3">
                <div className="min-w-0">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <LineChartIcon className="h-4 w-4" />
                        Analytics
                    </CardTitle>
                    <CardDescription className="truncate">
                        Live GA4 data
                        {gaCode && <> · <code className="font-mono text-[11px]">{gaCode}</code></>}
                        {gaPropertyId && <> · property <code className="font-mono text-[11px]">{gaPropertyId}</code></>}
                    </CardDescription>
                </div>
                <div className="flex items-center gap-2">
                    <RangeToggle range={range} onChange={setRange} />
                </div>
            </CardHeader>
            <CardContent>
                {!gaPropertyId ? (
                    <NoGaProperty />
                ) : reportQuery.isLoading ? (
                    <div className="flex items-center justify-center py-16">
                        <Spinner size="lg" />
                    </div>
                ) : !reportQuery.data?.hasData ? (
                    <CollectingData />
                ) : (
                    <DashboardBody data={reportQuery.data} realtime={realtimeQuery.data ?? null} />
                )}
            </CardContent>
        </Card>
    );
}

// ─── Range toggle ────────────────────────────────────────────────────────────
function RangeToggle({ range, onChange }: { range: AnalyticsRange; onChange: (r: AnalyticsRange) => void }): React.ReactNode {
    const options: AnalyticsRange[] = ["24h", "7d", "30d", "90d"];
    return (
        <div className="inline-flex rounded-md border bg-muted/30 p-0.5 text-xs">
            {options.map((o) => (
                <button
                    key={o}
                    type="button"
                    onClick={() => onChange(o)}
                    className={cn(
                        "rounded px-2.5 py-1 font-medium transition-colors",
                        range === o
                            ? "bg-background text-foreground shadow-sm"
                            : "text-muted-foreground hover:text-foreground"
                    )}
                >
                    {o}
                </button>
            ))}
        </div>
    );
}

// ─── Empty states ────────────────────────────────────────────────────────────
function NoGaProperty(): React.ReactNode {
    return (
        <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">GA4 not linked</p>
            <p className="mt-1">
                This domain doesn't have a GA4 property id yet. Set{" "}
                <code className="font-mono text-[11px]">GOOGLE_SERVICE_ACCOUNT_KEY</code> and{" "}
                <code className="font-mono text-[11px]">GA_ACCOUNT_ID</code> to enable auto-provisioning,
                then any new domain will land with analytics wired in.
            </p>
        </div>
    );
}

function CollectingData(): React.ReactNode {
    return (
        <div className="rounded-md border border-dashed p-6 text-sm text-muted-foreground">
            <p className="font-medium text-foreground">Collecting data…</p>
            <p className="mt-1">
                GA4 is linked but no sessions have landed yet. Fresh properties typically take 15–30 min before
                their first pageview shows up, and up to 24 h for full attribution.
            </p>
        </div>
    );
}

// ─── Main body ───────────────────────────────────────────────────────────────
function DashboardBody({ data, realtime }: { data: AnalyticsReportDto; realtime: RealtimeReportDto | null }): React.ReactNode {
    const convRate = data.sessions > 0 ? (data.conversions / data.sessions) * 100 : 0;
    const prevConvRate = data.previous.sessions > 0 ? (data.previous.conversions / data.previous.sessions) * 100 : 0;

    return (
        <div className="space-y-6">
            {/* Realtime bar */}
            <RealtimeBar realtime={realtime} />

            {/* KPI strip */}
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                <KpiCard
                    label="Sessions"
                    value={data.sessions}
                    prev={data.previous.sessions}
                />
                <KpiCard
                    label="Users"
                    value={data.users}
                    prev={data.previous.users}
                    sub={`${data.newUsers.toLocaleString()} new`}
                />
                <KpiCard
                    label="Conversions"
                    value={data.conversions}
                    prev={data.previous.conversions}
                    sub={data.sessions > 0 ? `${convRate.toFixed(1)}% rate` : undefined}
                />
                <KpiCard
                    label="Engagement"
                    value={`${(data.engagementRate * 100).toFixed(0)}%`}
                    rawValue={data.engagementRate}
                    prevRaw={data.previous.engagementRate}
                    sub={`${formatDuration(data.avgEngagementSec)} avg · ${(data.bounceRate * 100).toFixed(0)}% bounce`}
                />
            </div>

            {/* Sessions / users / conversions trend */}
            <Section title="Trend">
                {data.daily.length > 0 ? (
                    <DailyTrend points={data.daily} />
                ) : (
                    <ChartFallback />
                )}
            </Section>

            {/* Hourly */}
            {data.hourly.length > 0 && (
                <Section title="Last 24 hours" subtitle="Sessions per hour">
                    <HourlyChart points={data.hourly} />
                </Section>
            )}

            {/* Acquisition — channels first (the marketer view) */}
            <Section
                title="Channels"
                subtitle="Default channel grouping (Direct, Organic Search, Paid Social…)"
            >
                <BreakdownBars points={data.sessionsByChannel} />
            </Section>

            <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-4">
                <Section title="Top sources" subtitle="Last touch">
                    <BreakdownBars points={data.sessionsBySource} />
                </Section>
                <Section title="First-touch sources" subtitle="Where users first arrived">
                    <BreakdownBars points={data.firstUserSource} />
                </Section>
                <Section title="Top mediums">
                    <BreakdownBars points={data.sessionsByMedium} />
                </Section>
                <Section title="Top campaigns">
                    <BreakdownBars points={data.sessionsByCampaign} />
                </Section>
            </div>

            {/* Audience: world map (countries) */}
            <Section title="Where visitors are coming from" subtitle="Sessions by country">
                <div className="grid gap-6 lg:grid-cols-[1fr_minmax(0,260px)]">
                    <div className="rounded-lg border bg-muted/10 p-2">
                        <WorldMap countries={data.sessionsByCountry} />
                    </div>
                    <div>
                        <BreakdownBars
                            points={data.sessionsByCountry.map((c) => ({
                                name: countryLabel(c.name),
                                count: c.count,
                            }))}
                        />
                    </div>
                </div>
            </Section>

            {/* Cities */}
            {data.sessionsByCity.length > 0 && (
                <Section title="Top cities">
                    <BreakdownBars points={data.sessionsByCity} />
                </Section>
            )}

            <div className="grid gap-6 lg:grid-cols-2 xl:grid-cols-4">
                <Section title="Devices">
                    <DeviceDonut points={data.sessionsByDevice} />
                </Section>
                <Section title="Operating systems">
                    <BreakdownBars points={data.sessionsByOs} />
                </Section>
                <Section title="Browsers">
                    <BreakdownBars points={data.sessionsByBrowser} />
                </Section>
                <Section title="New vs returning">
                    <DeviceDonut points={data.newVsReturning} />
                </Section>
            </div>

            {/* Engagement: all events */}
            <Section title="Events" subtitle="All events fired in the range">
                <BreakdownBars points={data.events} maxItems={15} />
            </Section>

            {/* Landing + page engagement side by side */}
            <div className="grid gap-6 xl:grid-cols-2">
                <Section title="Top landing pages">
                    <LandingTable points={data.topLandingPages} />
                </Section>
                <Section title="Top pages by views" subtitle="Avg engagement per pageview">
                    <PagesTable points={data.topPages} />
                </Section>
            </div>

            {/* Footer hint */}
            {data.previous.sessions > 0 && (
                <p className="text-[11px] text-muted-foreground">
                    Δ compared to previous {data.range} ({data.previous.sessions.toLocaleString()} sessions,{" "}
                    {prevConvRate.toFixed(1)}% conversion).
                </p>
            )}
        </div>
    );
}

function Section({
    title, subtitle, children,
}: { title: string; subtitle?: string; children: React.ReactNode }): React.ReactNode {
    return (
        <div className="space-y-2">
            <div className="flex items-baseline justify-between">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h4>
                {subtitle && <span className="text-[11px] text-muted-foreground">{subtitle}</span>}
            </div>
            {children}
        </div>
    );
}

function ChartFallback(): React.ReactNode {
    return <p className="rounded-md border border-dashed p-6 text-center text-xs text-muted-foreground">No data yet.</p>;
}

// ─── Realtime ─────────────────────────────────────────────────────────────────
function RealtimeBar({ realtime }: { realtime: RealtimeReportDto | null }): React.ReactNode {
    const active30 = realtime?.activeUsers ?? 0;
    const active5 = realtime?.activeUsersLast5Min ?? 0;
    const live = active5 > 0;
    return (
        <div className="flex items-center gap-3 rounded-lg border bg-muted/20 p-3">
            <div className="relative">
                <Activity className={cn("h-4 w-4", live ? "text-emerald-500" : "text-muted-foreground")} />
                {live && (
                    <span className="absolute -top-0.5 -right-0.5 h-2 w-2 animate-pulse rounded-full bg-emerald-500" />
                )}
            </div>
            <div className="flex-1 text-sm">
                <span className="font-semibold tabular-nums">{active30.toLocaleString()}</span>{" "}
                <span className="text-muted-foreground">active right now</span>
                <span className="ml-3 text-xs text-muted-foreground">
                    {active5.toLocaleString()} in last 5 min
                </span>
            </div>
            {realtime && realtime.byEvent.length > 0 && (
                <div className="hidden flex-wrap items-center gap-1.5 text-[11px] md:flex">
                    {realtime.byEvent.slice(0, 4).map((e) => (
                        <span
                            key={e.name}
                            className="rounded-md border bg-background px-1.5 py-0.5 font-mono text-muted-foreground"
                        >
                            {e.name} · {e.count}
                        </span>
                    ))}
                </div>
            )}
        </div>
    );
}

// ─── KPI card with delta ─────────────────────────────────────────────────────
function KpiCard({
    label, value, prev, sub, rawValue, prevRaw,
}: {
    label: string;
    value: string | number;
    prev?: number;
    sub?: string;
    rawValue?: number;
    prevRaw?: number;
}): React.ReactNode {
    const cur = rawValue ?? (typeof value === "number" ? value : 0);
    const previous = prevRaw ?? prev ?? 0;
    const delta = previous > 0 ? ((cur - previous) / previous) * 100 : null;
    return (
        <div className="rounded-lg border p-4">
            <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-muted-foreground">{label}</span>
                {delta !== null && <Delta pct={delta} />}
            </div>
            <div className="mt-2 text-3xl font-semibold tracking-tight tabular-nums">
                {typeof value === "number" ? value.toLocaleString() : value}
            </div>
            {sub && <div className="mt-1 text-[11px] text-muted-foreground">{sub}</div>}
        </div>
    );
}

function Delta({ pct }: { pct: number }): React.ReactNode {
    if (Math.abs(pct) < 0.5) {
        return <span className="flex items-center gap-0.5 text-[11px] text-muted-foreground">
            <Minus className="h-3 w-3" /> 0%
        </span>;
    }
    const up = pct > 0;
    return (
        <span className={cn(
            "flex items-center gap-0.5 text-[11px] font-medium",
            up ? "text-emerald-600" : "text-rose-600"
        )}>
            {up ? <ArrowUpRight className="h-3 w-3" /> : <ArrowDownRight className="h-3 w-3" />}
            {Math.abs(pct).toFixed(0)}%
        </span>
    );
}

// ─── Charts ──────────────────────────────────────────────────────────────────
function DailyTrend({ points }: { points: AnalyticsReportDto["daily"] }): React.ReactNode {
    return (
        <div className="h-72 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={points} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                    <defs>
                        <linearGradient id="sessGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={SESSIONS_COLOUR} stopOpacity={0.35} />
                            <stop offset="95%" stopColor={SESSIONS_COLOUR} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="usersGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor={USERS_COLOUR} stopOpacity={0.25} />
                            <stop offset="95%" stopColor={USERS_COLOUR} stopOpacity={0} />
                        </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis
                        dataKey="date"
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={11}
                        tickFormatter={shortDate}
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} allowDecimals={false} />
                    <Tooltip content={<TrendTooltip />} />
                    <Legend wrapperStyle={{ fontSize: 11 }} iconType="circle" />
                    <Area
                        type="monotone"
                        dataKey="sessions"
                        stroke={SESSIONS_COLOUR}
                        strokeWidth={2}
                        fill="url(#sessGrad)"
                        name="Sessions"
                    />
                    <Area
                        type="monotone"
                        dataKey="users"
                        stroke={USERS_COLOUR}
                        strokeWidth={2}
                        fill="url(#usersGrad)"
                        name="Users"
                    />
                    <Line
                        type="monotone"
                        dataKey="conversions"
                        stroke={CONVERSIONS_COLOUR}
                        strokeWidth={2}
                        dot={{ r: 3 }}
                        name="Conversions"
                    />
                </AreaChart>
            </ResponsiveContainer>
        </div>
    );
}

function HourlyChart({ points }: { points: AnalyticsReportDto["hourly"] }): React.ReactNode {
    return (
        <div className="h-40 w-full">
            <ResponsiveContainer width="100%" height="100%">
                <BarChart data={points} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
                    <XAxis
                        dataKey="hour"
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={10}
                        tickFormatter={(h) => h.slice(11, 13)}
                        interval={2}
                    />
                    <YAxis stroke="hsl(var(--muted-foreground))" fontSize={10} allowDecimals={false} />
                    <Tooltip
                        formatter={(v: any) => [Number(v).toLocaleString(), "Sessions"]}
                        labelFormatter={(h: any) => String(h).replace("T", " ")}
                        contentStyle={tooltipStyle}
                    />
                    <Bar dataKey="sessions" radius={[3, 3, 0, 0]} fill={SESSIONS_COLOUR} />
                </BarChart>
            </ResponsiveContainer>
        </div>
    );
}

function DeviceDonut({ points }: { points: { name: string; count: number }[] }): React.ReactNode {
    if (points.length === 0) return <ChartFallback />;
    const total = points.reduce((s, p) => s + p.count, 0);
    return (
        <div className="flex items-center gap-4">
            <div className="relative h-32 w-32 shrink-0">
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                        <Pie
                            data={points}
                            dataKey="count"
                            nameKey="name"
                            innerRadius={36}
                            outerRadius={56}
                            paddingAngle={2}
                            stroke="none"
                        >
                            {points.map((_, i) => (
                                <Cell key={i} fill={PALETTE[i % PALETTE.length]} />
                            ))}
                        </Pie>
                        <Tooltip
                            formatter={(v: any) => Number(v).toLocaleString()}
                            contentStyle={tooltipStyle}
                        />
                    </PieChart>
                </ResponsiveContainer>
                <div className="absolute inset-0 flex items-center justify-center text-center">
                    <div>
                        <div className="text-lg font-semibold tabular-nums">{total.toLocaleString()}</div>
                        <div className="text-[10px] uppercase tracking-wide text-muted-foreground">total</div>
                    </div>
                </div>
            </div>
            <ul className="flex-1 space-y-1.5 text-sm">
                {points.map((p, i) => (
                    <li key={p.name} className="flex items-center gap-2">
                        <span
                            className="h-2.5 w-2.5 shrink-0 rounded-sm"
                            style={{ background: PALETTE[i % PALETTE.length] }}
                        />
                        <span className="capitalize">{p.name}</span>
                        <span className="ml-auto font-mono tabular-nums text-muted-foreground">
                            {p.count.toLocaleString()} · {((p.count / total) * 100).toFixed(0)}%
                        </span>
                    </li>
                ))}
            </ul>
        </div>
    );
}

function BreakdownBars({ points, maxItems = 8 }: { points: { name: string; count: number }[]; maxItems?: number }): React.ReactNode {
    if (points.length === 0) return <ChartFallback />;
    const max = Math.max(1, ...points.map((p) => p.count));
    return (
        <ul className="space-y-1.5 text-sm">
            {points.slice(0, maxItems).map((p) => (
                <li key={p.name} className="space-y-0.5">
                    <div className="flex items-center justify-between gap-2">
                        <span className="truncate font-medium">{p.name || "(not set)"}</span>
                        <span className="font-mono text-xs text-muted-foreground tabular-nums">
                            {p.count.toLocaleString()}
                        </span>
                    </div>
                    <div className="h-1.5 rounded-full bg-muted">
                        <div
                            className="h-1.5 rounded-full bg-primary/70"
                            style={{ width: `${(p.count / max) * 100}%` }}
                        />
                    </div>
                </li>
            ))}
        </ul>
    );
}

function PagesTable({ points }: { points: AnalyticsReportDto["topPages"] }): React.ReactNode {
    if (points.length === 0) return <ChartFallback />;
    return (
        <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
                <thead className="bg-muted/30 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2 text-left font-medium">Page</th>
                        <th className="px-3 py-2 text-right font-medium">Views</th>
                        <th className="px-3 py-2 text-right font-medium">Avg engagement</th>
                    </tr>
                </thead>
                <tbody className="divide-y">
                    {points.map((p) => (
                        <tr key={p.path}>
                            <td className="max-w-[280px] truncate px-3 py-2 align-top">
                                <div className="font-mono text-xs">{p.path || "/"}</div>
                                {p.title && <div className="truncate text-[11px] text-muted-foreground">{p.title}</div>}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums">{p.views.toLocaleString()}</td>
                            <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                                {formatDuration(p.avgEngagementSec)}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

function LandingTable({ points }: { points: AnalyticsReportDto["topLandingPages"] }): React.ReactNode {
    if (points.length === 0) return <ChartFallback />;
    return (
        <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
                <thead className="bg-muted/30 text-[11px] uppercase tracking-wide text-muted-foreground">
                    <tr>
                        <th className="px-3 py-2 text-left font-medium">Path</th>
                        <th className="px-3 py-2 text-right font-medium">Sessions</th>
                        <th className="px-3 py-2 text-right font-medium">Conversions</th>
                        <th className="px-3 py-2 text-right font-medium">Conv rate</th>
                    </tr>
                </thead>
                <tbody className="divide-y">
                    {points.map((p) => {
                        const rate = p.sessions > 0 ? (p.conversions / p.sessions) * 100 : 0;
                        return (
                            <tr key={p.path}>
                                <td className="px-3 py-2 font-mono text-xs">{p.path || "/"}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{p.sessions.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right tabular-nums">{p.conversions.toLocaleString()}</td>
                                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                                    {p.sessions > 0 ? `${rate.toFixed(1)}%` : "—"}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

// ─── Tooltip + helpers ───────────────────────────────────────────────────────
const tooltipStyle: React.CSSProperties = {
    background: "hsl(var(--background))",
    border: "1px solid hsl(var(--border))",
    borderRadius: 6,
    fontSize: 12,
    padding: "6px 10px",
};

function TrendTooltip({ active, payload, label }: any): React.ReactNode {
    if (!active || !payload || payload.length === 0) return null;
    return (
        <div style={tooltipStyle}>
            <div className="mb-1 font-medium">{label}</div>
            {payload.map((p: any) => (
                <div key={p.name} className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-sm" style={{ background: p.color }} />
                    <span className="text-muted-foreground">{p.name}</span>
                    <span className="ml-auto font-mono tabular-nums">{Number(p.value).toLocaleString()}</span>
                </div>
            ))}
        </div>
    );
}

function shortDate(d: string): string {
    if (!d || d.length < 10) return d;
    return d.slice(5).replace("-", "/");
}

function formatDuration(seconds: number): string {
    if (!Number.isFinite(seconds) || seconds <= 0) return "0s";
    if (seconds < 60) return `${Math.round(seconds)}s`;
    const m = Math.floor(seconds / 60);
    const s = Math.round(seconds - m * 60);
    return s === 0 ? `${m}m` : `${m}m ${s}s`;
}

function countryLabel(code: string): string {
    if (!code || code.length !== 2 || code === "(not set)") return "Unknown";
    try {
        const flag = code.toUpperCase().split("")
            .map((c) => String.fromCodePoint(0x1f1a5 + c.charCodeAt(0)))
            .join("");
        const display = new Intl.DisplayNames(["en"], { type: "region" }).of(code.toUpperCase());
        return `${flag} ${display ?? code}`;
    } catch {
        return code;
    }
}
